import Vue from 'vue';
import App from './App.vue';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import Router from 'vue-router';
import LoadProfileDetails from './components/LoadProfileDetails.vue';
import AuthPage from './pages/AuthPage.vue';
import ProcessRunner from './components/ProcessRunner.vue';
import redirect from './components/redirect.vue';
import Impressum from './components/Impressum.vue';
import Privacy from './components/Privacy.vue';
import Home from './components/Home.vue';
import AccessDenied from './components/AccessDenied.vue';
import PageNotFound from './components/PageNotFound.vue';

//amplify
import Amplify, * as AmplifyModules from 'aws-amplify';
import { AmplifyPlugin } from 'aws-amplify-vue';
import awsconfig from './aws-exports-custom';
import { AmplifyEventBus } from 'aws-amplify-vue';
import store from './store/store';
import CryptoJS from 'crypto-js';
import {
  CognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

// Define userpool based on URL
const cognitoClient = awsconfig.codelos;
const crypto_secret = process.env.VUE_APP_CRYPTO_SECRET;
// if (location.href.includes('.team-energie.')) {
//   cognitoClient = awsconfig.teamenergie;
// }

// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: cognitoClient.aws_user_pools_id,
  ClientId: cognitoClient.aws_user_pools_web_client_id,
});

Amplify.configure(cognitoClient);
Vue.use(AmplifyPlugin, AmplifyModules);

import { BootstrapVue, IconsPlugin } from 'bootstrap-vue';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';

Vue.use(BootstrapVue);
Vue.use(IconsPlugin);

//vue-recource for restful apis
import VueResource from 'vue-resource';
Vue.use(VueResource);

import AsyncComputed from 'vue-async-computed';
Vue.use(AsyncComputed);

import vueKanban from 'vue-kanban';
import { isUserNotLoggedIn } from './router/guards/auth.guard';
Vue.use(vueKanban);

Vue.use(Router);
Vue.config.productionTip = false;

import { pageBuilderPlugins } from '@codelos.io/page-builder';
import { putUserLoginDetails } from './services/axios/data.api';
Vue.use(pageBuilderPlugins);

let user;
let mounted = false;

AmplifyEventBus.$on('authState', async (state) => {
  if (state === 'signedOut') {
    // Reset all values
    mounted = false;
    store.commit('clearStore');
    router.push({ path: '/auth' });
  } else if (state === 'signedIn') {
    user = await getUser();
    let path = decodeURIComponent(
      router.history.current.fullPath
        .replace('/auth?redirect=', '')
        .replace('/auth', '/')
    );
    router.push({ path: path });
  }
});

async function getUser(isFlowAndDatabaseItemRequired = true) {
  return Vue.prototype.$Amplify.Auth.currentAuthenticatedUser()
    .then(async (data) => {
      if (data && data.signInUserSession) {
        store.commit('setUser', data);

        // Set isAdmin
        const email = data.attributes.email;
        const adminMails = [
          'codelos.io',
          'esparkinfo.com',
          'esparkbiztech.com',
          'esparkbiztechnologies.com',
        ];
        const isJanik = email == 'janik@codelos.io';
        if (adminMails.includes(email.split('@')[1]) && !isJanik) {
          store.commit('setIsAdmin', true);
        } else {
          store.commit('setIsAdmin', false);
        }

        if (!mounted) {
          // Set loading
          store.commit('setLoading', true);
          // Store user log in database when sign in
          putUserLoginDetails();

          // Get all resources
          if (isFlowAndDatabaseItemRequired) {
            store.dispatch('getFlows');
            store.dispatch('fetchFlowAccessLevel');
            store.dispatch('getUserSettings');
            store.dispatch('fetchWorkFlowTags');
            await store.dispatch('getUserSettingsDB');
          }
          mounted = true;

          // Remov loading
          store.commit('setLoading', false);
        }
        return data;
      }
    })
    .catch(() => {
      store.commit('setUser', null);
      return null;
    });
}

async function postEncryptedMessageToParent() {
  try {
    if (window?.parent) {
      const session = await AmplifyModules.Auth.currentSession();

      // Extract tokens
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();

      const data = {
        idToken,
        accessToken,
        refreshToken,
      };
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(data),
        crypto_secret
      ).toString();
      window.parent.postMessage({ type: 'Auth', payload: encryptedData }, '*');
    }
  } catch (error) {
    console.log('error while posting message to parent:>> ', error);
  }
}

function decryptAuthMessage(encryptedData) {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, crypto_secret);
    const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    return decryptedData;
  } catch (error) {
    return null;
  }
}

// Function to handle login via posted message
function setupMessageListener() {
  window.addEventListener('message', async (event) => {
    const data = event.data;
    if (data.type == 'AuthDetails' && data?.payload) {
      try {
        const decryptedData = decryptAuthMessage(data.payload);
        if (
          !decryptedData &&
          !decryptedData.idToken &&
          !decryptedData.accessToken &&
          !decryptedData.refreshToken
        )
          return;
        // Extract tokens from the message
        const { idToken, accessToken, refreshToken } = decryptedData;

        // Create token objects
        const idTokenObj = new CognitoIdToken({ IdToken: idToken });
        const accessTokenObj = new CognitoAccessToken({
          AccessToken: accessToken,
        });
        const refreshTokenObj = new CognitoRefreshToken({
          RefreshToken: refreshToken,
        });

        // Manually set the session
        const session = new CognitoUserSession({
          IdToken: idTokenObj,
          AccessToken: accessTokenObj,
          RefreshToken: refreshTokenObj,
        });

        // Attach session to the Cognito user
        const cognitoUser = new CognitoUser({
          Username: session.getIdToken().decodePayload().email, // Ensure this matches your application logic
          Pool: userPool,
        });

        cognitoUser.setSignInUserSession(session);
        if (window?.parent) {
          window.parent.postMessage(
            {
              type: 'Authorized',
              payload: { authorized: true },
            },
            '*'
          );
        }
      } catch (error) {
        console.log('Error setting user session:', error);
        if (window?.parent) {
          window.parent.postMessage(
            {
              type: 'Authorized',
              payload: { authorized: false },
            },
            '*'
          );
        }
      }
    }
  });
}

// Call the function to set up the listener
setupMessageListener();

// Catch errors from router
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err);
};

const routes = [
  {
    path: '/load-profile-details',
    name: 'loadProfileDetails',
    component: LoadProfileDetails,
    meta: { requiresAuth: true },
  },

  {
    path: '/process-runner/:id',
    name: 'process-runner',
    component: ProcessRunner,
    props: true,
    meta: { requiresAuth: false },
  },
  {
    path: '/auth',
    name: 'auth',
    component: AuthPage,
    props: true,
    beforeEnter: isUserNotLoggedIn,
  },
  {
    path: '/impressum',
    name: 'impressum',
    component: Impressum,
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: Privacy,
  },
  {
    path: '/',
    name: 'home',
    component: Home,
    props: (route) => ({
      section: route.query.section,
      database: route.query.database,
      processID: route.query.process,
      itemID: route.query.item,
    }),
    meta: { requiresAuth: true },
  },
  {
    path: '/redirect/:id',
    name: 'redirect',
    component: redirect,
    props: true,
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: AccessDenied,
  },
  {
    path: '/page-not-found',
    name: 'pageNotFound',
    component: PageNotFound,
  },
  {
    path: '/meeting-subscription',
    name: 'meeting-subscription',
    component: () => import('./pages/MeetingSubscriptionPage.vue'),
  },
  {
    path: '(.*)',
    component: PageNotFound,
  },
];

//components.Authenticator
export const router = new Router({
  routes,
});

router.beforeResolve(async (to, from, next) => {
  if (window?.parent) {
    if (from?.name == 'auth') postEncryptedMessageToParent();
    window.parent.postMessage(
      {
        type: 'ROUTE_CHANGED',
        payload: { fullPath: to?.fullPath, query: to?.query },
      },
      '*'
    );
  }
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const flowList = store.getters.getAllFlows;
    if (from.name === 'process-runner' && !flowList.length) {
      mounted = false;
    }
    user = await getUser();
    if (!user) {
      return next({
        path: '/auth',
        query: {
          redirect: to.fullPath,
        },
      });
    }
    // }

    return next();
  } else if (
    to.matched.some((record) => !record.meta.requiresAuth) &&
    to.name === 'process-runner'
  ) {
    user = await getUser(false);
  } else if (to.name == 'auth') {
    mounted = false;
  }
  return next();
});

new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount('#app');
